import { OrganizersPanel } from './OrganizersPanel';
import { usePersonalLinkCopied } from '@src/pages/BoxPage/usePersonalLinkCopied';
import { BoxActivity } from './BoxActivity';
import { FullGameUpgradeCard } from './FullGameUpgradeCard';
import { ThemeIcon } from '@src/theme/ThemeIcon';
import { useParticipantPreview } from '@src/pages/BoxPage/ParticipantPreview';
import { useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BoxMode, normalizeQuickBoxName, type BoxInfo, type CardInfo } from '@lub0v/santa-utils';
import {
  Badge,
  GiftIcon,
  Input,
  LinkIcon,
  MailIcon,
  PackageIcon,
  PencilIcon,
  TrashIcon,
  Stack,
  Typography,
  WarningIcon,
  closeDialog,
  showDialog,
  showToast,
  useParticipantTableViewport,
  type MenuItem,
} from '@src/components';
import {
  DistributionMethodCard,
  DistributionMethods,
  ParticipantRow,
  ParticipantTable,
  ParticipantTableFill,
  ParticipantTableRowList,
  QuickDrawHero,
} from '@src/components';
import { invitedCount, revealedCount } from '@src/pages/BoxPage/dashboard/dashboardStats';
import { resolveApiErrorKey } from '@src/i18n/apiError';
import { sameOriginUrl } from '@src/routing/urls';
import { ACCOUNT_BOXES_PATH } from '@src/routing/routes';
import { EmailInviteForm, emailInviteError, emailInviteSendError } from '@src/pages/BoxPage/EmailInviteForm';
import { editBoxInput } from '@src/pages/BoxPage/boxInput';
import { InviteMessageForm } from '@src/pages/BoxPage/InviteMessageForm';
import { SecretPair } from '@src/pages/BoxPage/SecretPair';
import {
  buildPersonalInviteMessage,
  buildSharedInviteMessage,
  composeInviteClipboard,
} from '@src/pages/BoxPage/inviteMessage';
import { useLinkInvitedIds } from '@src/pages/BoxPage/linkInviteStorage';
import { participantStatus, participantStatusTone } from '@src/pages/BoxPage/participantStatus';
import { sortOrganizerCards } from '@src/pages/BoxPage/sortCards';
import { santaClient } from '@src/state/client';

/** Which per-participant dialog is on screen, and what it is working on. */
type RowDialog =
  | { kind: 'copy'; card: CardInfo; link: string; loading: boolean }
  | {
      kind: 'email';
      card: CardInfo;
      email: string;
      consent: boolean;
      emailError: string | null;
      error: string | null;
    }
  | { kind: 'rename'; card: CardInfo; name: string; error: string | null }
  | { kind: 'shared' };

/** A pair the organizer asked to see. */
interface PairRequest {
  giver: CardInfo;
  receiver: CardInfo;
  /** Which side was asked about, which is what the question is phrased around. */
  kind: 'giftee' | 'santa';
}

export interface OrganizerDashboardProps {
  info: BoxInfo;
  cards: CardInfo[];
  /** Re-reads the box after anything that changed it on the server. */
  onChanged: () => Promise<void> | void;
}

/**
 * What the organizer of a finished quick draw sees: the name of the game, the
 * three ways of handing out results, and a row per participant showing how far
 * that person has got.
 */
export function OrganizerDashboard({ info, cards, onChanged }: OrganizerDashboardProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const openParticipantPreview = useParticipantPreview();
  const box = info.box;
  const [dialog, setDialog] = useState<RowDialog | null>(null);
  const [copyMessage, setCopyMessage] = useState('');
  const [sharedMessage, setSharedMessage] = useState('');
  const participantsRef = useRef<HTMLElement>(null);
  const [pair, setPair] = useState<PairRequest | null>(null);
  /*
   * The game name is edited in its own state rather than in `dialog`, because
   * it belongs to the box and not to a row: keeping them apart stops a row
   * dialog from reopening every time the title changes.
   */
  const [renamingBox, setRenamingBox] = useState(false);
  const [boxName, setBoxName] = useState(box.name);

  const cardIds = useMemo(() => cards.map((card) => card.id), [cards]);
  const { copiedSet, markLinkInvited } = useLinkInvitedIds(box.key, cardIds);
  const recordLinkCopied = usePersonalLinkCopied(box.key, markLinkInvited, onChanged);
  const isActiveDraw = Boolean(info.isActiveDraw);
  const archived = Boolean(box.isArchived);
  const invited = useMemo(() => invitedCount(cards, copiedSet), [cards, copiedSet]);
  const revealed = useMemo(() => revealedCount(cards), [cards]);
  const scrollToParticipants = useCallback(() => {
    participantsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);
  /* The one link everyone can use, pointed at the site the organizer is on. */
  const sharedLink = info.inviteLink ? sameOriginUrl(info.inviteLink) : '';

  const sortItems = useCallback(
    (list: CardInfo[]) => sortOrganizerCards(list, { unassignedFirst: isActiveDraw }),
    [isActiveDraw],
  );
  const getSearchText = useCallback((card: CardInfo) => card.username || '', []);
  const { tableRef: participantTableRef, ...participants } = useParticipantTableViewport({
    items: cards,
    getSearchText,
    sortItems,
    alwaysSearch: true,
    showSearch: true,
  });

  /* --- actions ----------------------------------------------------------- */

  const copyToClipboard = useCallback(
    async (text: string): Promise<boolean> => {
      try {
        await navigator.clipboard.writeText(text);
        return true;
      } catch {
        showToast(t('box.organizer.copyFailed'));
        return false;
      }
    },
    [t],
  );

  /**
   * The dialog shows the real link, so it is issued as the dialog opens rather
   * than when copy is pressed. The backend reuses the same link for the card,
   * so opening this dialog again does not invalidate previously shared links.
   */
  async function openCopyDialog(card: CardInfo) {
    setCopyMessage(
      buildPersonalInviteMessage({ username: card.username, boxName: box.name, isActiveDraw }, t),
    );
    setDialog({
      kind: 'copy',
      card,
      link: '',
      loading: true,
    });
    const result = await santaClient.createCardAccessLink({ key: box.key, id: card.id });
    if (!result.ok || !result.data?.url) {
      setDialog(null);
      closeDialog();
      showToast(t('box.organizer.linkFailed'));
      return;
    }
    // Shared and opened by someone else, so it has to point at the site this
    // organizer is looking at rather than at the API's own frontend setting.
    const url = sameOriginUrl(result.data.url);
    setDialog((current) =>
      current?.kind === 'copy' && current.card.id === card.id
        ? { ...current, link: url, loading: false }
        : current,
    );
  }

  const confirmCopy = useCallback(
    async (state: Extract<RowDialog, { kind: 'copy' }>) => {
      const text = composeInviteClipboard(copyMessage, state.link, [
        t('box.invite.personal.openLink'),
        t('box.invite.shared.openLink'),
      ]);
      if (await copyToClipboard(text)) {
        void recordLinkCopied(state.card.id);
        showToast(t('box.organizer.messageCopied'), 'success');
      }
      setDialog(null);
    },
    [copyMessage, copyToClipboard, recordLinkCopied, t],
  );

  const confirmShared = useCallback(async () => {
    const text = composeInviteClipboard(sharedMessage, sharedLink, [
      t('box.invite.shared.openLink'),
      t('box.invite.personal.openLink'),
    ]);
    if (await copyToClipboard(text)) {
      showToast(t('box.organizer.messageCopied'), 'success');
    }
    setDialog(null);
  }, [sharedMessage, copyToClipboard, sharedLink, t]);

  const sendEmail = useCallback(
    async (state: Extract<RowDialog, { kind: 'email' }>) => {
      const email = state.email.trim();
      const emailError = emailInviteError(email, t);
      if (emailError || !state.consent) {
        setDialog({ ...state, emailError: emailError ?? null });
        throw new Error('invalid email');
      }
      const result = await santaClient.sendCardLink({ key: box.key, id: state.card.id, email });
      if (!result.ok) {
        setDialog({ ...state, error: emailInviteSendError(result.error, t) });
        // Thrown so the dialog host keeps the dialog open with the error shown.
        throw new Error('send failed');
      }
      setDialog(null);
      showToast(t('box.organizer.emailSent', { name: state.card.username }), 'success');
      await onChanged();
    },
    [box.key, onChanged, t],
  );

  const renameParticipant = useCallback(
    async (state: Extract<RowDialog, { kind: 'rename' }>) => {
      const username = state.name.trim();
      if (username === state.card.username) {
        setDialog(null);
        return;
      }
      // Two people with the same name cannot be told apart on any of the
      // screens that follow, so this is caught here rather than at the server.
      const taken = cards.some(
        (card) =>
          card.id !== state.card.id &&
          normalizeQuickBoxName(card.username) === normalizeQuickBoxName(username),
      );
      if (taken) {
        setDialog({ ...state, error: 'randomizer.problem.duplicate' });
        throw new Error('duplicate name');
      }
      const result = await santaClient.updateOtherCard({
        key: box.key,
        id: state.card.id,
        username,
      });
      if (!result.ok) {
        setDialog({ ...state, error: result.error?.message ?? 'errors.UNKNOWN' });
        throw new Error('rename failed');
      }
      setDialog(null);
      await onChanged();
    },
    [box.key, cards, onChanged],
  );

  const renameBox = useCallback(
    async (name: string) => {
      const trimmed = name.trim();
      if (!trimmed || trimmed === box.name) {
        return;
      }
      const result = await santaClient.updateBox({ ...editBoxInput(info), name: trimmed });
      if (!result.ok) {
        showToast(t('box.organizer.renameFailed'));
        throw new Error('rename failed');
      }
      await onChanged();
    },
    [box.name, info, onChanged, t],
  );

  async function convertToFullGame() {
    const result = await santaClient.updateBox({ ...editBoxInput(info), mode: BoxMode.FULL });
    if (!result.ok) {
      showToast(t('box.organizer.convertFailed'));
      throw new Error('convert failed');
    }
    await onChanged();
  }

  async function setArchived(next: boolean) {
    // Only key/name/isArchived — the API applies archive first, then rejects
    // any other field as a change to an archived box.
    const result = await santaClient.updateBox({
      key: box.key,
      name: box.name,
      isArchived: next,
    });
    if (!result.ok) {
      showToast(t(next ? 'box.organizer.archiveFailed' : 'box.organizer.restoreFailed'));
      throw new Error('archive failed');
    }
    await onChanged();
  }

  async function deleteGame() {
    const result = await santaClient.deleteBox({ key: box.key });
    if (!result.ok) {
      showToast(t(result.error?.message || 'newBox.saveFailed'));
      throw new Error('delete failed');
    }
    navigate(ACCOUNT_BOXES_PATH);
  }

  /* --- dialogs ----------------------------------------------------------- */

  /*
   * Every dialog here is re-shown whenever its state changes: the host renders
   * a snapshot, so this effect is what makes a typed character or a ticked
   * consent box appear. Same pattern as the randomizer's exclusion editor.
   */
  useEffect(() => {
    if (!dialog) {
      return;
    }

    if (dialog.kind === 'copy') {
      showDialog({
        title: t('box.organizer.copyLink.title'),
        mobileFullScreen: true,
        children: (
          <InviteMessageForm
            message={copyMessage}
            onMessageChange={(message) => {
              setCopyMessage(message);
            }}
            messageLabel={t('box.invite.messageLabel')}
            messageHint={t('box.invite.linkPlaceholderHint')}
            link={dialog.link}
            onLinkCopied={() => { void recordLinkCopied(dialog.card.id); }}
            linkLabel={t('box.organizer.copyLink.linkLabel')}
            warning={t('box.organizer.copyLink.warning')}
            testId="box-copy-form"
          />
        ),
        cancel: t('box.cancel'),
        submit: t('box.organizer.copyLink.submit'),
        submitDisabled: !dialog.link,
        onSubmit: () => confirmCopy(dialog),
        onCancel: () => setDialog(null),
        testId: 'box-copy-dialog',
      });
      return;
    }

    if (dialog.kind === 'shared') {
      showDialog({
        title: t('box.organizer.shared.title'),
        mobileFullScreen: true,
        children: (
          <InviteMessageForm
            message={sharedMessage}
            onMessageChange={(message) => {
              setSharedMessage(message);
            }}
            messageLabel={t('box.invite.messageLabel')}
            messageHint={t('box.invite.linkPlaceholderHint')}
            link={sharedLink}
            linkLabel={t('box.organizer.shared.linkLabel')}
            warning={t('box.organizer.shared.warning')}
            testId="box-shared-form"
          />
        ),
        cancel: t('box.cancel'),
        submit: t('box.organizer.shared.submit'),
        onSubmit: () => confirmShared(),
        onCancel: () => setDialog(null),
        testId: 'box-shared-dialog',
      });
      return;
    }

    if (dialog.kind === 'email') {
      showDialog({
        title: t('box.organizer.email.title', { name: dialog.card.username }),
        children: (
          <EmailInviteForm
            intro={t('box.organizer.email.body')}
            email={dialog.email}
            onEmailChange={(email) =>
              setDialog((current) =>
                current?.kind === 'email'
                  ? { ...current, email, emailError: null, error: null }
                  : current,
              )
            }
            onEmailBlur={() =>
              setDialog((current) =>
                current?.kind === 'email'
                  ? { ...current, emailError: emailInviteError(current.email, t) ?? null }
                  : current,
              )
            }
            emailLabel={t('box.organizer.email.label')}
            hint={t('box.organizer.email.hint')}
            consentGiven={dialog.consent}
            onConsentChange={(consent) =>
              setDialog((current) =>
                current?.kind === 'email' ? { ...current, consent } : current,
              )
            }
            emailError={dialog.emailError}
            error={dialog.error}
            testId="box-email-form"
          />
        ),
        cancel: t('box.cancel'),
        submit: t('box.organizer.email.submit'),
        submitDisabled: !dialog.consent || !dialog.email.trim() || Boolean(dialog.emailError),
        onSubmit: () => sendEmail(dialog),
        onCancel: () => setDialog(null),
        testId: 'box-email-dialog',
      });
      return;
    }

    showDialog({
      title: t('box.organizer.renameParticipant.title'),
      children: (
        <Input
          value={dialog.name}
          onValueChange={(name) =>
            setDialog((current) =>
              current?.kind === 'rename' ? { ...current, name, error: null } : current,
            )
          }
          label={t('box.organizer.renameParticipant.label')}
          error={dialog.error ? t(resolveApiErrorKey(dialog.error)) : undefined}
          testId="box-rename-participant-input"
        />
      ),
      cancel: t('box.cancel'),
      submit: t('box.save'),
      submitDisabled: dialog.name.trim().length < 2,
      onSubmit: () => renameParticipant(dialog),
      onCancel: () => setDialog(null),
      testId: 'box-rename-participant-dialog',
    });
  }, [
    copyMessage,
    sharedMessage,
    confirmCopy,
    recordLinkCopied,
    confirmShared,
    dialog,
    renameParticipant,
    sendEmail,
    sharedLink,
    t,
  ]);

  useEffect(() => {
    if (!renamingBox) {
      return;
    }
    showDialog({
      title: t('box.organizer.renameBox.title'),
      children: (
        <Input
          value={boxName}
          onValueChange={setBoxName}
          label={t('box.organizer.renameBox.label')}
          error={boxName.trim() ? undefined : t('box.organizer.renameBox.empty')}
          testId="box-rename-input"
        />
      ),
      cancel: t('box.cancel'),
      submit: t('box.save'),
      submitDisabled: !boxName.trim(),
      onSubmit: async () => {
        await renameBox(boxName);
        setRenamingBox(false);
      },
      onCancel: () => setRenamingBox(false),
      testId: 'box-rename-dialog',
    });
  }, [renamingBox, boxName, renameBox, t]);

  /*
   * Seeing a pair is a two-step action, as in v1: a draw the organizer is
   * playing in stays secret unless they say a second time that they want to
   * know.
   */
  useEffect(() => {
    if (!pair) {
      return;
    }
    const subject = pair.kind === 'santa' ? pair.receiver : pair.giver;
    showDialog({
      title: t('box.organizer.giftee.pairTitle'),
      children: (
        <SecretPair
          santa={pair.giver}
          giftee={pair.receiver}
          question={t(
            pair.kind === 'santa'
              ? 'box.organizer.giftee.confirmSanta'
              : 'box.organizer.giftee.confirmGiftee',
            { name: subject.username },
          )}
          revealLabel={t('box.organizer.giftee.confirmYes')}
          santaLabel={t('box.organizer.giftee.santa')}
          gifteeLabel={t('box.organizer.giftee.giftee')}
          testId="box-giftee"
        />
      ),
      onCancel: () => setPair(null),
      testId: 'box-giftee-dialog',
    });
  }, [pair, t]);

  function confirmConvert() {
    showDialog({
      title: t('box.organizer.convert.title'),
      children: t('box.organizer.convert.body'),
      cancel: t('box.cancel'),
      submit: t('box.organizer.convert.submit'),
      onSubmit: () => convertToFullGame(),
      testId: 'box-convert-dialog',
    });
  }

  function openSharedDialog() {
    setSharedMessage(buildSharedInviteMessage({ boxName: box.name, isActiveDraw }, t));
    setDialog({ kind: 'shared' });
  }

  function confirmArchive() {
    const archived = Boolean(box.isArchived);
    showDialog({
      title: t(archived ? 'box.organizer.restore.title' : 'box.organizer.archive.title'),
      children: t(archived ? 'box.organizer.restore.body' : 'box.organizer.archive.body'),
      cancel: t('box.cancel'),
      submit: t(archived ? 'box.organizer.restore.submit' : 'box.organizer.archive.submit'),
      onSubmit: () => setArchived(!archived),
      testId: 'box-archive-dialog',
    });
  }

  function confirmDelete() {
    showDialog({
      title: t('game.settings.deleteTitle'),
      children: t('game.settings.deleteBody'),
      cancel: t('box.cancel'),
      submit: t('game.settings.delete'),
      submitVariant: 'danger',
      onSubmit: () => deleteGame(),
      testId: 'box-delete-dialog',
    });
  }

  function headerMenu(): MenuItem[] {
    const items: MenuItem[] = [
      archived
        ? {
            id: 'restore',
            label: t('box.organizer.menu.restoreGame'),
            icon: <PackageIcon />,
            onSelect: confirmArchive,
          }
        : {
            id: 'archive',
            label: t('box.organizer.menu.archiveGame'),
            icon: <PackageIcon />,
            onSelect: confirmArchive,
          },
      {
        id: 'delete',
        label: t('box.organizer.menu.deleteGame'),
        icon: <TrashIcon />,
        danger: true,
        onSelect: confirmDelete,
      },
    ];
    return items;
  }

  function rowMenu(card: CardInfo): MenuItem[] {
    const archived = Boolean(box.isArchived);
    const items: MenuItem[] = [
      {
        id: 'rename',
        label: t('box.organizer.menu.rename'),
        icon: <PencilIcon />,
        disabled: archived,
        onSelect: () => setDialog({ kind: 'rename', card, name: card.username, error: null }),
      },
    ];
    // The organizer's own card needs no invitation: they are already here.
    if (!card.isYou) {
      if (!card.isVerified) {
        items.push({
          id: 'copy',
          label: t('box.organizer.menu.copy'),
          icon: <ThemeIcon name="link" fallback={<LinkIcon />} />,
          disabled: archived,
          onSelect: () => void openCopyDialog(card),
        });
      }
      items.push({
        id: 'email',
        label: t('box.organizer.menu.email'),
        icon: <ThemeIcon name="mail" fallback={<MailIcon />} />,
        disabled: archived,
        onSelect: () =>
          setDialog({
            kind: 'email',
            card,
            email: '',
            consent: false,
            emailError: null,
            error: null,
          }),
      });
    }
    const giftee = cards.find((person) => person.id === card.wardId);
    const santa = cards.find((person) => person.wardId === card.id);
    if (isActiveDraw && giftee) {
      items.push({
        id: 'giftee',
        label: t('box.organizer.menu.showGiftee'),
        icon: <GiftIcon />,
        onSelect: () => setPair({ giver: card, receiver: giftee, kind: 'giftee' }),
      });
    }
    if (isActiveDraw && santa) {
      items.push({
        id: 'santa',
        label: t('box.organizer.menu.showSanta'),
        icon: <GiftIcon />,
        onSelect: () => setPair({ giver: santa, receiver: card, kind: 'santa' }),
      });
    }
    return items;
  }

  /* --- render ------------------------------------------------------------ */

  return (
    <Stack gap={8} className="box-dashboard" testId="box-dashboard">
      <QuickDrawHero
        name={box.name}
        picture={box.picture}
        logo={box.logo}
        lead={t('box.organizer.hero.lead')}
        participantsValue={String(cards.length)}
        participantsLabel={t('box.organizer.hero.stats.participants', { count: cards.length })}
        invitedValue={String(invited)}
        invitedLabel={t('box.organizer.hero.stats.invited', { count: invited })}
        revealedValue={String(revealed)}
        revealedLabel={t('box.organizer.hero.stats.revealed', { count: revealed })}
        onRename={() => {
          setBoxName(box.name);
          setRenamingBox(true);
        }}
        renameLabel={t('box.organizer.renameBox.title')}
        renameDisabled={archived}
        archivedBadge={
          archived ? (
            <Badge tone="neutral" testId="box-archived">
              {t('box.organizer.archive.badge')}
            </Badge>
          ) : undefined
        }
        menu={headerMenu()}
        menuLabel={t('box.organizer.menu.game')}
        testId="box-dashboard-header"
      />

      {archived ? null : (
        <DistributionMethods
          title={t('box.organizer.methods.title')}
          lead={t('box.organizer.methods.lead')}
          testId="box-distribute"
        >
          <DistributionMethodCard
            icon={<LinkIcon />}
            tone="success"
            title={t('box.organizer.methods.personal.title')}
            badge={t('box.organizer.methods.personal.badge')}
            body={t('box.organizer.methods.personal.body')}
            hint={t('box.organizer.methods.personal.hint')}
            onClick={scrollToParticipants}
            testId="box-method-personal"
          />
          <DistributionMethodCard
            icon={<MailIcon />}
            tone="support"
            title={t('box.organizer.methods.email.title')}
            body={t('box.organizer.methods.email.body')}
            hint={t('box.organizer.methods.email.hint')}
            onClick={scrollToParticipants}
            testId="box-method-email"
          />
          <DistributionMethodCard
            icon={<WarningIcon />}
            tone="warning"
            title={t('box.organizer.methods.shared.title')}
            badge={t('box.organizer.methods.shared.badge')}
            body={t('box.organizer.methods.shared.body')}
            action={{
              label: t('box.organizer.methods.shared.button'),
              onClick: openSharedDialog,
            }}
            testId="box-method-shared"
          />
        </DistributionMethods>
      )}

      <OrganizersPanel info={info} cards={cards} onChanged={onChanged} />
      <BoxActivity key={info.box.key} info={info} cards={cards} />

      <ParticipantTableFill ref={participantsRef} as="section" viewportPage>
        <ParticipantTable
          ref={participantTableRef}
          showSearch={participants.showSearch}
          search={{
            value: participants.query,
            onChange: participants.setSearch,
            placeholder: t('box.organizer.searchPlaceholder'),
            clearLabel: t('box.organizer.searchClear'),
          }}
          isEmpty={participants.isEmpty}
          empty={
            <Typography as="p" role="body/base" testId="box-participants-empty">
              {t('box.organizer.noResults')}
            </Typography>
          }
          pagination={{
            page: participants.page,
            pageCount: participants.pageCount,
            onChange: participants.setPage,
            total: participants.filtered.length,
            pageSize: participants.pageSize ?? participants.filtered.length,
            previousLabel: t('box.organizer.pages.previous'),
            nextLabel: t('box.organizer.pages.next'),
            pageLabel: (value) => t('box.organizer.pages.page', { page: value }),
            testId: 'box-pages',
          }}
          testId="box-participants"
        >
          <ParticipantTableRowList
            placeholderCount={participants.placeholderCount}
            testId="box-participant-list"
          >
            {participants.visible.map((card, index) => {
              const statusKey = participantStatus(card, copiedSet);
              return (
                <ParticipantRow
                  key={card.id}
                  card={card}
                  index={(participants.page - 1) * (participants.pageSize ?? 0) + index}
                  ordinal={(participants.page - 1) * (participants.pageSize ?? 0) + index + 1}
                  showOrdinal={false}
                  status={
                    statusKey === 'not_sent' ? undefined : t(`box.organizer.status.${statusKey}`)
                  }
                  statusTone={
                    statusKey === 'not_sent' ? undefined : participantStatusTone(statusKey)
                  }
                  youLabel={card.isYou ? t('box.organizer.thatsYou') : undefined}
                  locked={archived}
                  previewLabel={t('box.organizer.preview.title', { name: card.username })}
                  onPreview={() => openParticipantPreview({ onChanged, isActiveDraw: Boolean(info.isActiveDraw),
                    card, people: cards, box: info.box,
                    index: (participants.page - 1) * (participants.pageSize ?? 0) + index,
                    copiedIds: copiedSet,
                  })}
                  onRename={() =>
                    setDialog({ kind: 'rename', card, name: card.username, error: null })
                  }
                  onCopy={
                    card.isYou || card.isVerified ? undefined : () => void openCopyDialog(card)
                  }
                  copyLabel={t('box.organizer.menu.copy')}
                  onEmail={
                    card.isYou
                      ? undefined
                      : () =>
                          setDialog({
                            kind: 'email',
                            card,
                            email: '',
                            consent: false,
                            emailError: null,
                            error: null,
                          })
                  }
                  emailLabel={t('box.organizer.menu.email')}
                  menu={rowMenu(card)}
                  menuLabel={t('box.organizer.menu.more')}
                  testId={`box-participant-${card.id}`}
                />
              );
            })}
          </ParticipantTableRowList>
        </ParticipantTable>
      </ParticipantTableFill>


      <FullGameUpgradeCard archived={archived} onExpand={confirmConvert} />
    </Stack>
  );
}
